import { Card, CardContent } from '@ui/card.tsx';
import { Button } from '@ui/button.tsx';
import { AlertCircle, CheckCircle, Download, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { unparse } from 'papaparse';
import { downloadCSV, cn } from '@r/utils/utils.ts';
import type { ImportAnalysis, ImportError } from '@r/hooks/use-file-parser-patch-with-errors.ts';

interface BaseErrorRow {
  rowKey: string;
  id: number;
  error: string;
}

interface PerformanceErrorRow extends BaseErrorRow {
  amount: string;
  label: string;
  realizedAt: string;
  realizedBy: string;
  company: string;
}

interface PointErrorRow extends BaseErrorRow {
  user: string;
  company: string;
  amount: string;
  label: string;
  type: string;
  valueDate: string;
  expirationDate: string;
}

interface CompanyErrorRow extends BaseErrorRow {
  name: string;
  uniqueCode: string;
  siren: string;
  tva: string;
  address: string;
  zipcode: string;
  city: string;
  country: string;
  responsible: string;
  typology: string;
}

interface UserErrorRow extends BaseErrorRow {
  email: string;
  firstname: string;
  lastname: string;
  uniqueCode: string;
  company: string;
  role: string;
  phone: string;
}

interface ReferenceErrorRow extends BaseErrorRow {
  brand: string;
  productLine: string;
  name: string;
  uniqueCode: string;
  pointValue: string;
}

interface TargetErrorRow extends BaseErrorRow {
  company: string;
  targetReference: string;
  targetValue: string;
}

type ErrorRow =
  | PerformanceErrorRow
  | PointErrorRow
  | CompanyErrorRow
  | UserErrorRow
  | ReferenceErrorRow
  | TargetErrorRow;

interface ImportErrorPageProps {
  errors: ImportError[];
  originalFile: File;
  onRetry: () => Promise<void> | void;
  onNewFile: () => void;
  importType: 'performances' | 'points' | 'companies' | 'users' | 'references' | 'targets';
  analysis?: ImportAnalysis | null;
}

export function ImportErrorPage({
  errors,
  onRetry,
  onNewFile,
  importType,
  analysis
}: ImportErrorPageProps) {
  const { t } = useTranslation();
  const [isRetrying, setIsRetrying] = useState(false);
  const hasErrors = errors.length > 0;
  const importAnalysis = analysis ?? {
    totalLines: 0,
    errorLines: hasErrors ? errors.length : 0,
    importableLines: 0,
    errorPercentage: 0
  };

  /** Génère le rapport CSV des erreurs en conservant les données de la ligne d'origine. */
  const downloadErrorsFile = () => {
    const errorData = errors.map((error, index) => {
      const data = {
        id: index + 1,
        message: error.message,
        line: error.line || '',
        field: error.field || '',
        value: error.value || '',
        error: error.error || '',
        originalData: error.originalData ? JSON.stringify(error.originalData) : ''
      };

      return importType === 'points'
        ? {
            ...data,
            expirationDate: error.originalData?.expirationDate?.toString() || ''
          }
        : data;
    });

    const csv = unparse(errorData);
    downloadCSV(csv, `${importType}_import_errors.csv`);
  };

  /** Retourne les libellés de colonnes adaptés au type d'import affiché. */
  const getColumnHeaders = () => {
    switch (importType) {
      case 'performances':
        return [
          t('import.error.table.header.id'),
          t('import.error.table.header.amount'),
          t('import.error.table.header.label'),
          t('import.error.table.header.realized.at'),
          t('import.error.table.header.realized.by'),
          t('import.error.table.header.company'),
          t('import.error.table.header.error')
        ];
      case 'points':
        return [
          t('import.error.table.header.id'),
          t('import.error.table.header.user'),
          t('import.error.table.header.company'),
          t('import.error.table.header.amount'),
          t('import.error.table.header.label'),
          t('import.error.table.header.type'),
          t('import.error.table.header.value.date'),
          t('import.error.table.header.expiration.date'),
          t('import.error.table.header.error')
        ];
      case 'companies':
        return [
          t('import.error.table.header.id'),
          t('import.error.table.header.name'),
          t('import.error.table.header.unique.code'),
          t('import.error.table.header.siret'),
          t('import.error.table.header.tva'),
          t('import.error.table.header.address'),
          t('import.error.table.header.zipcode'),
          t('import.error.table.header.city'),
          t('import.error.table.header.country'),
          t('import.error.table.header.responsible'),
          t('import.error.table.header.typology'),
          t('import.error.table.header.error')
        ];
      case 'users':
        return [
          t('import.error.table.header.id'),
          t('import.error.table.header.email'),
          t('import.error.table.header.firstname'),
          t('import.error.table.header.lastname'),
          t('import.error.table.header.unique.code'),
          t('import.error.table.header.company'),
          t('import.error.table.header.role'),
          t('import.error.table.header.phone'),
          t('import.error.table.header.error')
        ];
      case 'references':
        return [
          t('import.error.table.header.id'),
          t('import.error.table.header.brand'),
          t('import.error.table.header.product.line'),
          t('import.error.table.header.name'),
          t('import.error.table.header.unique.code'),
          t('import.error.table.header.point.value'),
          t('import.error.table.header.error')
        ];
      case 'targets':
        return [
          t('import.error.table.header.id'),
          t('import.error.table.header.company'),
          t('import.error.table.header.name'),
          t('import.error.table.header.amount'),
          t('import.error.table.header.error')
        ];
      default:
        return [t('import.error.table.header.id'), t('import.error.table.header.error')];
    }
  };

  /** Transforme les erreurs techniques du parseur en lignes lisibles dans le tableau. */
  const parseErrorData = (errors: ImportError[]): ErrorRow[] => {
    return errors.slice(0, 50).map((error, index) => {
      const explicitLine = typeof error.line === 'number' ? error.line : undefined;
      const lineMatch = error.message.match(/Ligne (\d+):/);
      const lineMatch2 = error.message.match(/Line (\d+)/);
      const line = explicitLine
        ? explicitLine
        : lineMatch
          ? Number.parseInt(lineMatch[1], 10)
          : lineMatch2
            ? Number.parseInt(lineMatch2[1], 10)
            : index + 1;

      const fieldFromProp = error.field;
      const fieldFromFr = error.message.match(/^Ligne \d+: ([^:]+): /)?.[1];
      const fieldFromEn = error.message.match(/^Line \d+ \| Property ([^ ]+) : /)?.[1];
      const field = fieldFromProp || fieldFromFr || fieldFromEn || '';

      const errorText = error.message
        .replace(/^(Ligne|Line) \d+: [^:]+: /, '')
        .replace(/^Line \d+ \| Property [^:]+ : /, '');

      const originalData = error.originalData || {};
      const baseData = {
        rowKey: `${line}:${field}:${error.error ?? errorText}:${JSON.stringify(originalData)}`,
        id: line,
        error: field ? `${field} ${errorText}` : errorText
      };

      switch (importType) {
        case 'performances':
          return {
            ...baseData,
            amount: originalData.amount?.toString() || '',
            label: originalData.label?.toString() || '',
            realizedAt: originalData.realizedAt?.toString() || '',
            realizedBy: originalData.userUniqueCode?.toString() || '',
            company: originalData.companyUniqueCode?.toString() || ''
          } as PerformanceErrorRow;
        case 'points':
          return {
            ...baseData,
            user: originalData.userUniqueCode?.toString() || '',
            company: originalData.companyUniqueCode?.toString() || '',
            amount: originalData.amount?.toString() || '',
            label: originalData.label?.toString() || '',
            type: originalData.typeLabel?.toString() || '',
            valueDate: originalData.valueDate?.toString() || '',
            expirationDate: originalData.expirationDate?.toString() || ''
          } as PointErrorRow;
        case 'companies':
          return {
            ...baseData,
            name: originalData.name?.toString() || '',
            uniqueCode: originalData.uniqueCode?.toString() || '',
            siren: originalData.siren?.toString() || '',
            tva: originalData.tva?.toString() || '',
            address: originalData.addressLine1?.toString() || '',
            zipcode: originalData.zipcode?.toString() || '',
            city: originalData.city?.toString() || '',
            country: originalData.countryAlpha3?.toString() || '',
            responsible: originalData.responsiblesUniqueCodes?.toString() || '',
            typology: originalData.typologyName?.toString() || ''
          } as CompanyErrorRow;
        case 'users': {
          const userWorkspace = originalData.userWorkspace as Record<string, unknown> | undefined;
          return {
            ...baseData,
            email: originalData.email?.toString() || '',
            firstname: originalData.firstname?.toString() || '',
            lastname: originalData.lastname?.toString() || '',
            uniqueCode: userWorkspace?.uniqueCode?.toString() || '',
            company: userWorkspace?.companyUniqueCode?.toString() || '',
            role: userWorkspace?.maxRole?.toString() || '',
            phone: userWorkspace?.phone?.toString() || ''
          } as UserErrorRow;
        }
        case 'targets':
          return {
            ...baseData,
            company: originalData.companyUniqueCode?.toString() || '',
            targetReference: originalData.targetReference?.toString() || '',
            targetValue: originalData.targetValue?.toString() || ''
          } as TargetErrorRow;
        default:
          return baseData as ErrorRow;
      }
    });
  };

  const errorData = parseErrorData(errors);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-bold">{t('import.error.page.title')}</h1>
      </div>

      <Card className={hasErrors ? 'bg-blue-50 border-blue-200' : 'bg-green-50 border-green-200'}>
        <CardContent className="pt-6">
          <div className="flex gap-4 items-start">
            {hasErrors ? (
              <AlertCircle className="mt-1 w-6 h-6 text-blue-600" />
            ) : (
              <CheckCircle className="mt-1 w-6 h-6 text-green-600" />
            )}
            <div className="space-y-2">
              <h3
                className={
                  hasErrors ? 'font-semibold text-blue-900' : 'font-semibold text-green-900'
                }
              >
                {hasErrors ? t('import.mapping.partial.title') : t('import.mapping.ready.title')}
              </h3>
              <p className={hasErrors ? 'text-blue-900' : 'text-green-900'}>
                {hasErrors
                  ? t('import.mapping.partial.description', {
                      percentage: importAnalysis.errorPercentage,
                      count: importAnalysis.errorLines
                    })
                  : t('import.mapping.ready.description')}
              </p>
              <p
                className={
                  hasErrors ? 'font-semibold text-blue-900' : 'font-semibold text-green-900'
                }
              >
                {t('import.mapping.validation.description', {
                  importableLines: importAnalysis.importableLines,
                  totalLines: importAnalysis.totalLines
                })}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {hasErrors ? (
        <Card>
          <CardContent className="pt-6">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b">
                    {getColumnHeaders().map((header) => (
                      <th key={header} className="p-3 font-medium text-left text-gray-700">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {errorData.map((row) => (
                    <tr key={row.rowKey} className="border-b hover:bg-gray-50">
                      <td className="p-3">{row.id}</td>
                      {importType === 'performances' && (
                        <>
                          <td className="p-3">{(row as PerformanceErrorRow).amount}</td>
                          <td className="p-3">{(row as PerformanceErrorRow).label}</td>
                          <td className="p-3">{(row as PerformanceErrorRow).realizedAt}</td>
                          <td className="p-3">{(row as PerformanceErrorRow).realizedBy}</td>
                          <td className="p-3">{(row as PerformanceErrorRow).company}</td>
                        </>
                      )}
                      {importType === 'points' && (
                        <>
                          <td className="p-3">{(row as PointErrorRow).user}</td>
                          <td className="p-3">{(row as PointErrorRow).company}</td>
                          <td className="p-3">{(row as PointErrorRow).amount}</td>
                          <td className="p-3">{(row as PointErrorRow).label}</td>
                          <td className="p-3">{(row as PointErrorRow).type}</td>
                          <td className="p-3">{(row as PointErrorRow).valueDate}</td>
                          <td className="p-3">{(row as PointErrorRow).expirationDate}</td>
                        </>
                      )}
                      {importType === 'companies' && (
                        <>
                          <td className="p-3">{(row as CompanyErrorRow).name}</td>
                          <td className="p-3">{(row as CompanyErrorRow).uniqueCode}</td>
                          <td className="p-3">{(row as CompanyErrorRow).siren}</td>
                          <td className="p-3">{(row as CompanyErrorRow).tva}</td>
                          <td className="p-3">{(row as CompanyErrorRow).address}</td>
                          <td className="p-3">{(row as CompanyErrorRow).zipcode}</td>
                          <td className="p-3">{(row as CompanyErrorRow).city}</td>
                          <td className="p-3">{(row as CompanyErrorRow).country}</td>
                          <td className="p-3">{(row as CompanyErrorRow).responsible}</td>
                          <td className="p-3">{(row as CompanyErrorRow).typology}</td>
                        </>
                      )}
                      {importType === 'users' && (
                        <>
                          <td className="p-3">{(row as UserErrorRow).email}</td>
                          <td className="p-3">{(row as UserErrorRow).firstname}</td>
                          <td className="p-3">{(row as UserErrorRow).lastname}</td>
                          <td className="p-3">{(row as UserErrorRow).uniqueCode}</td>
                          <td className="p-3">{(row as UserErrorRow).company}</td>
                          <td className="p-3">{(row as UserErrorRow).role}</td>
                          <td className="p-3">{(row as UserErrorRow).phone}</td>
                        </>
                      )}
                      {importType === 'references' && (
                        <>
                          <td className="p-3">{(row as ReferenceErrorRow).brand}</td>
                          <td className="p-3">{(row as ReferenceErrorRow).productLine}</td>
                          <td className="p-3">{(row as ReferenceErrorRow).name}</td>
                          <td className="p-3">{(row as ReferenceErrorRow).uniqueCode}</td>
                          <td className="p-3">{(row as ReferenceErrorRow).pointValue}</td>
                        </>
                      )}
                      {importType === 'targets' && (
                        <>
                          <td className="p-3">{(row as TargetErrorRow).company}</td>
                          <td className="p-3">{(row as TargetErrorRow).targetReference}</td>
                          <td className="p-3">{(row as TargetErrorRow).targetValue}</td>
                        </>
                      )}
                      <td className="p-3">
                        <div className="flex gap-2 items-center">
                          <span className="text-red-600">{row.error}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <div className="flex flex-wrap gap-4">
        <Button
          variant={hasErrors ? 'default' : undefined}
          className={cn(
            !hasErrors && 'bg-green-600 text-white hover:bg-green-700 focus-visible:ring-green-600'
          )}
          disabled={isRetrying}
          onClick={async () => {
            if (isRetrying) return;
            try {
              setIsRetrying(true);
              const ret = onRetry?.();
              if (ret && typeof (ret as Promise<void>).then === 'function') {
                await (ret as Promise<void>);
              }
            } finally {
              setIsRetrying(false);
            }
          }}
        >
          {!hasErrors ? <CheckCircle className="w-4 h-4" /> : null}
          {t('import.mapping.validate.button')}
        </Button>
        <Button variant="outline" onClick={onNewFile}>
          <Upload className="mr-2 w-4 h-4" />
          {t('import.error.page.new.file.button')}
        </Button>
        {hasErrors ? (
          <Button variant="outline" onClick={downloadErrorsFile}>
            <Download className="mr-2 w-4 h-4" />
            {t('import.error.page.download.errors.button')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
